import { useState } from "react";
import { makePostApiCall, makeGetApiCall } from "../../shared/makeApiCall";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { generateUniqueNumber } from "../../shared/utility";
export function EquipmentMaintenanceViewModel(logout, addSnackbar) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [maintenances, setMaintenances] = useState({ data: [] });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  /**
   * Fetch paginated list of equipment maintenance records
   * @param {string} search - Search term for equipment name or verified_by
   * @param {number} page - Page number for pagination
   */
  const getMaintenance = async function (search, page) {
    setLoading(true);
    const payload = {
      page: page || 1,
      search: search || "",
    };
    const callResult = await makePostApiCall("equipmentMaintenances", payload);
    setLoading(false);
    if (callResult.success) {
      setMaintenances(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal("Equipment maintenance record error");
      }
    }
  };

  /**
   * Get a single equipment maintenance record by ID
   * @param {number} id - Maintenance record ID
   * @returns {Object|null} Maintenance record or null if not found
   */
  const getMaintenanceItemByID = async function (id) {
    setLoading(true);
    const callResult = await makeGetApiCall(`equipmentMaintenances/${id}`);
    setLoading(false);
    if (callResult.success) {
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal("Equipment maintenance record Error");
      }
      return null;
    }
  };

  /**
   * Create a new equipment maintenance record
   * @param {Object} values - Form values containing equipment_id, requested_by, maintenance_date, etc.
   */
  const createMaintenance = async function (values) {
    // values will be FormData from form for document uploads
    const callResult = await makePostApiCall(
      "equipmentMaintenances/create",
      values,
      "multipart/form-data"
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Equipment maintenance record created successfully",
          SnackbarType.Success
        )
      );
      setSlideOver({
        childComponent: null,
        open: false,
      });
      getMaintenance("", 1);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal("Equipment maintenance record error");
      }
    }
  };

  /**
   * Update an existing equipment maintenance record
   * @param {number} id - Maintenance record ID
   * @param {Object} values - Form values to update (only include fields to update)
   */
  const updateMaintenance = async function (id, values) {
    // values will be FormData from form for document uploads
    const callResult = await makePostApiCall(
      `equipmentMaintenances/update/${id}`,
      values,
      "multipart/form-data"
    );

    if (callResult.success) {
      const snackbarId = `${Date.now()}`;
      addSnackbar(
        new SnackbarData(
          snackbarId,
          "Equipment maintenance record updated successfully",
          SnackbarType.Success
        )
      );
      setSlideOver({
        childComponent: null,
        open: false,
      });
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal("Equipment maintenance record error");
      }
      return null;
    }
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  /**
   * Delete an equipment maintenance record (soft delete)
   * @param {number} id - Maintenance record ID
   */
  const deleteMaintenance = async function (id) {
    setLoading(true);
    const callResult = await makePostApiCall(
      `equipmentMaintenances/delete/${id}`,
      {}
    );
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getMaintenance("", 1);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Equipment maintenance record deleted successfully",
          SnackbarType.Success
        )
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  /**
   * Show delete confirmation modal
   * @param {Object} maintenance - Maintenance record to delete
   */
  const askDeleteMaintenance = function (maintenance) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteMaintenance(maintenance.id)}
          message={`Are you sure you want to delete this maintenance record for ${maintenance.equipment_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  return {
    askDeleteMaintenance,
    createMaintenance,
    deleteMaintenance,
    emptyModal,
    getMaintenanceItemByID,
    getMaintenance,
    loading,
    maintenances,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    updateMaintenance,
  };
}
